/**
 * WS4 Session Transcript modal — legacy `.modal-shell` chrome (dark
 * bg + true-blue border + halo, 12 px radius) hosting a tabbed view of
 * the recorded workshop transcript.
 *
 * Ported from:
 *   - `_legacy/wd.html` lines 1485-1718 — `#transcriptModal` shell:
 *     `.modal-shell`, `.modal-header`, `.dt-tab-bar`/`.dt-tab`,
 *     `.modal-body` containing per-pane `.trx-block` rows.
 *   - `_legacy/xaes.css` lines 1060-1108, 1343-1364, 1390-1413 — chrome
 *     and `.trx-*` / `.sp-*` styling (ported verbatim to `src/index.css`).
 *   - `_legacy/ws4_live_content.js` lines 1-88 — `panes.full`, `panes.act1`,
 *     `panes.act2`, `panes.act3` content arrays (ported to
 *     `transcript-content.ts`).
 *
 * The vanilla version was static markup with `onclick="trxActivateTab(…)"`
 * inline; here Radix's `Tabs` primitive drives the same `dt-tab-active`
 * class swap. `PanelDialogContent` bypasses shadcn's built-in top-right
 * ✕ so the legacy `.modal-close-btn` in the header is the sole close
 * affordance.
 */

import { useEffect, useMemo, useState } from "react";
import { MessageSquare, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { useTranscripts, type Transcript } from "@/stores/useTranscripts";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { resolveSessionDate } from "@/features/workshop/session-date";
import { MediaTranscriptPane } from "./MediaTranscriptPane";
import { parseTranscriptBody } from "./transcript-cues";
import { deriveTranscriptSections } from "@/features/ws4-playback/derive-playback";
import {
  getTranscript,
  type TranscriptBlock,
  type TranscriptTabKey,
  type TranscriptTag,
} from "./transcript-content";

/** Stable empty reference — avoids a new array each render (selector identity). */
const EMPTY_TRANSCRIPTS: Transcript[] = [];

type TranscriptPlaybackProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

/**
 * Speaker → vanilla `.sp-*` class. Mirrors `_legacy/xaes.css` lines
 * 1358-1364. Unknown speakers fall back to `.sp-group` (muted italic)
 * so participant additions degrade gracefully.
 */
const SPEAKER_CLASS: Record<string, string> = {
  "Jordan K.": "sp-jordan",
  "XAES-AI": "sp-aria",
  "John C.": "sp-ben",
  "Priya S.": "sp-priya",
  "Marcus W.": "sp-marcus",
  "Sam R.": "sp-sam",
};

const TAG_LABEL: Record<TranscriptTag, string> = {
  action: "ACTION",
  flag: "RISK FLAG",
  note: "NOTE",
  ai: "AI OBSERVER",
};

const TAG_CLASS: Record<TranscriptTag, string> = {
  action: "trx-tag trx-tag-action",
  flag: "trx-tag trx-tag-flag",
  note: "trx-tag trx-tag-note",
  ai: "trx-tag trx-tag-ai",
};

function BannerRow({ banner }: { banner: Extract<TranscriptBlock, { kind: "banner" }> }) {
  const toneStyle =
    banner.tone === "gold"
      ? { background: "rgba(var(--accent-gold-rgb), 0.04)" }
      : banner.tone === "green"
        ? { background: "rgba(var(--accent-green-rgb), 0.04)" }
        : undefined;
  const labelStyle =
    banner.tone === "gold"
      ? { color: "var(--accent-gold)" }
      : banner.tone === "green"
        ? { color: "var(--accent-green)" }
        : undefined;
  return (
    <div className="trx-section-banner" style={toneStyle}>
      <span className="trx-section-label" style={labelStyle}>
        {banner.label}
      </span>
      <span className="trx-section-time">{banner.time}</span>
    </div>
  );
}

function LineRow({ line }: { line: Extract<TranscriptBlock, { kind: "line" }> }) {
  const speakerClass = SPEAKER_CLASS[line.speaker] ?? "sp-group";
  const tag = line.tag;
  const tagText = line.tagText ?? (tag ? TAG_LABEL[tag] : undefined);
  return (
    <div className="trx-block">
      <div className="trx-row">
        <span className="trx-time">{line.time}</span>
        <span className={cn("trx-speaker", speakerClass)}>{line.speaker}</span>
        <div className="trx-text">
          {line.text}
          {tag && tagText && (
            <span className={TAG_CLASS[tag]}>{tagText}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function Pane({ blocks }: { blocks: TranscriptBlock[] }) {
  return (
    <div className="dt-pane dt-pane-active" style={{ padding: 0 }}>
      {blocks.map((b) =>
        b.kind === "banner" ? (
          <BannerRow key={b.id} banner={b} />
        ) : (
          <LineRow key={b.id} line={b} />
        ),
      )}
    </div>
  );
}

export function TranscriptPlayback({
  open,
  onOpenChange,
  workshopId,
}: TranscriptPlaybackProps) {
  const [activeKey, setActiveKey] = useState<TranscriptTabKey>("full");
  const [activeTranscriptId, setActiveTranscriptId] = useState<string | null>(null);

  // Real uploaded transcripts take precedence over the authored WS4 demo.
  const realTranscripts = useTranscripts(
    (s) => s.entriesByWorkshop[workshopId as Workshop["id"]] ?? EMPTY_TRANSCRIPTS,
  );
  // Hydrate when opened — the modal can be reached from surfaces that don't
  // otherwise mount the Repository view (e.g. the Asset Locker card).
  useEffect(() => {
    if (open) void useTranscripts.getState().hydrate();
  }, [open]);

  const hasReal = realTranscripts.length > 0;
  const activeTranscript = hasReal
    ? (realTranscripts.find((t) => t.id === activeTranscriptId) ?? realTranscripts[0])
    : undefined;

  // Break the uploaded transcript into chapter sections (UAT round 5).
  // "Synced View" is the player + full cue list (MediaTranscriptPane);
  // the derived chapter tabs render the same lines grouped + tagged.
  const sections = useMemo(
    () =>
      activeTranscript
        ? deriveTranscriptSections(parseTranscriptBody(activeTranscript.body))
        : [],
    [activeTranscript],
  );
  const chapterSections = sections.filter((s) => s.key !== "full");
  const [sectionKey, setSectionKey] = useState<string>("player");
  useEffect(() => {
    setSectionKey("player");
  }, [activeTranscript?.id]);
  const activeSection = chapterSections.find((s) => s.key === sectionKey);

  // RT-GALLERY — authored fixture fallback (the per-workshop getter). `null`
  // for workshops with no authored transcript → a per-workshop empty state.
  const tabs = getTranscript(workshopId);
  const longTitle =
    WORKSHOPS.find((w) => w.id === workshopId)?.longTitle ?? workshopId;
  // Session date — derived from the (override-aware) agenda date instead of
  // the frozen seed literal (UAT: live dates).
  const override = useEffectiveWorkshopOverride(workshopId);
  const sessionDate = resolveSessionDate(workshopId, override);
  const activeTab = tabs
    ? (tabs.find((t) => t.key === activeKey) ?? tabs[0])
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          width: "82vw",
          maxWidth: 940,
          maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
        }}
      >
        <DialogTitle className="sr-only">Session Transcript</DialogTitle>
        <DialogDescription className="sr-only">
          Auto-transcribed workshop session — {workshopId}
        </DialogDescription>

        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)" }}
        >
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-[10px]">
              <MessageSquare
                aria-hidden
                style={{ width: 14, height: 14, color: "var(--accent-gold)" }}
              />
              <span className="modal-title">SESSION TRANSCRIPT</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-gold-rgb), 0.12)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                  borderRadius: 10,
                  fontSize: "var(--fs-label)",
                  color: "var(--accent-gold)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                ASSET LOCKER
              </span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-label)",
                  color: "var(--accent-sky)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                {workshopId}
              </span>
            </div>
            <div className="flex items-center gap-[6px]">
              <span
                style={{
                  fontSize: "var(--fs-label)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                {hasReal ? "UPLOADED TRANSCRIPT" : "AUTO-TRANSCRIBED · XAES-AI"}
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {hasReal ? (
            <>
              {/* Tab bar — one tab per uploaded transcript */}
              {realTranscripts.length > 1 && (
                <div className="dt-tab-bar" role="tablist" aria-label="Uploaded transcripts">
                  {realTranscripts.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTranscript?.id === t.id}
                      onClick={() => setActiveTranscriptId(t.id)}
                      className={cn("dt-tab", activeTranscript?.id === t.id && "dt-tab-active")}
                    >
                      {t.title}
                    </button>
                  ))}
                </div>
              )}

              {/* Section bar — the derived chapter breakdown. Only when
                  the transcript actually splits (synced, >1 chapter). */}
              {chapterSections.length > 0 && (
                <div className="dt-tab-bar" role="tablist" aria-label="Transcript sections">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeSection === undefined}
                    onClick={() => setSectionKey("player")}
                    className={cn("dt-tab", activeSection === undefined && "dt-tab-active")}
                  >
                    Synced View
                  </button>
                  {chapterSections.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      role="tab"
                      aria-selected={sectionKey === s.key}
                      onClick={() => setSectionKey(s.key)}
                      className={cn("dt-tab", sectionKey === s.key && "dt-tab-active")}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Body — player + synced cue list, or a chapter section */}
              <div
                className="modal-body"
                style={{ padding: 0, display: "flex", flexDirection: "column", minHeight: 0 }}
              >
                {activeSection ? (
                  <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    <Pane blocks={activeSection.blocks} />
                  </div>
                ) : (
                  activeTranscript && (
                    <MediaTranscriptPane
                      key={activeTranscript.id}
                      transcript={activeTranscript}
                    />
                  )
                )}
              </div>
            </>
          ) : tabs ? (
            <>
              {/* Meta strip */}
              <div
                style={{
                  display: "flex",
                  flexShrink: 0,
                  alignItems: "center",
                  gap: 20,
                  padding: "8px 16px",
                  background: "var(--bg-panel-alt)",
                  borderBottom: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
                  flexWrap: "wrap",
                }}
              >
                <MetaCell
                  label="Date"
                  // Live session date (override-aware agenda date); the
                  // seed literal stays only as the unauthored fallback.
                  value={sessionDate ?? "14 Mar 2026"}
                />
                <MetaCell label="Duration" value="3h 28m" />
                <MetaCell label="Participants" value="24" />
                <MetaCell label="Facilitator" value="Jordan K." />
                <MetaCell label="AI Observer" value="XAES-AI" valueColor="var(--accent-sky)" />
                <div
                  style={{
                    marginLeft: "auto",
                    fontSize: "var(--fs-label)",
                    color: "var(--text-muted)",
                    letterSpacing: 1,
                  }}
                >
                  Accuracy:{" "}
                  <span style={{ color: "var(--accent-green)", fontWeight: 700 }}>97.3%</span>
                </div>
              </div>

              {/* Tab bar */}
              <div className="dt-tab-bar" role="tablist" aria-label="Transcript sections">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={activeKey === t.key}
                    onClick={() => setActiveKey(t.key)}
                    className={cn("dt-tab", activeKey === t.key && "dt-tab-active")}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Body */}
              <div className="modal-body" style={{ padding: 0 }}>
                {activeTab && <Pane blocks={activeTab.blocks} />}
              </div>
            </>
          ) : (
            // RT-GALLERY — per-workshop empty state (no authored transcript).
            <div className="modal-body" style={{ padding: "40px 16px" }}>
              <p
                style={{
                  textAlign: "center",
                  color: "var(--text-secondary)",
                  fontSize: "var(--fs-sub)",
                  lineHeight: 1.6,
                }}
              >
                No session transcript registered for workshop{" "}
                <code>{workshopId}</code> yet. Paste or upload one from the
                workshop's capture form to populate this view.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">
              {workshopId} — {longTitle}
            </span>
            <span className="modal-footer-right">
              {hasReal
                ? `${realTranscripts.length} transcript${realTranscripts.length === 1 ? "" : "s"} uploaded`
                : "3h 28m · 14 ACTIONS · 3 RISK FLAGS · AUTO-TRANSCRIBED"}
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

function MetaCell({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <span
      style={{
        fontSize: "var(--fs-label)",
        color: "var(--text-muted)",
        letterSpacing: 1,
        textTransform: "uppercase",
      }}
    >
      {label}:{" "}
      <span style={{ color: valueColor ?? "var(--text-secondary)" }}>{value}</span>
    </span>
  );
}
